/**
 * React Router starts a View Transition for page changes (`AppLink`, D-103) and skips the running one when a
 * new navigation arrives. The browser then rejects the *skipped* transition's `ready` promise with an
 * `AbortError` — the API's normal way of saying "this one was skipped" — and since nobody listens to `ready`
 * the rejection is reported as an uncaught error: `Uncaught (in promise) AbortError: Transition was skipped`.
 * That is console noise (Lighthouse's "errors logged to the console" check), a false alarm for error
 * monitoring, and it fails any test that watches for page errors (found by the E2E suite, intermittently
 * on a phone viewport: tests/e2e/specs/appearance.spec.ts).
 *
 * Only `ready` is quieted. `updateCallbackDone` and `finished` reject when the page's own update callback
 * throws — that is a real error and must stay visible.
 */

const MARK = Symbol.for('sbg.view-transitions.quiet');

type Patched = Document & { [MARK]?: true };

/** Make `document.startViewTransition` handle the `ready` rejection of a skipped transition. Idempotent. */
export function quietSkippedViewTransitions(doc: Document = document): void {
  const target: Patched = doc;
  if (typeof target.startViewTransition !== 'function' || target[MARK] === true) return;
  const original = target.startViewTransition.bind(target);
  target.startViewTransition = (options) => {
    const transition = original(options);
    // a no-op handler: the rejection is expected, and marking it handled is all that is wanted
    transition.ready.catch(() => undefined);
    return transition;
  };
  target[MARK] = true;
}
